import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSensorDetail } from '../hooks/useInactiveSensors';
import { SENSOR_STATUS_MAP, type InactiveSensorStatus } from '../types';

interface SensorDetailSheetProps {
  sensorId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1.5 border-b last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%] break-words">{value || '-'}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </h4>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function FlagBadge({ label, active }: { label: string; active: boolean }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {label}: {active ? 'Yes' : 'No'}
    </span>
  );
}

function TagList({ items }: { items: string[] }) {
  if (items.length === 0) return <span className="text-muted-foreground">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item) => (
        <Badge key={item} variant="secondary" className="text-xs">
          {item}
        </Badge>
      ))}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="mt-6 space-y-6">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-3/4" />
        </div>
      ))}
    </div>
  );
}

export function SensorDetailSheet({ sensorId, open, onOpenChange }: SensorDetailSheetProps) {
  const { data: sensor, isLoading, error } = useSensorDetail(sensorId);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            {isLoading ? (
              <Skeleton className="h-6 w-48" />
            ) : sensor ? (
              <>
                <span className="truncate">{sensor.name}</span>
                {sensor.status in SENSOR_STATUS_MAP && (
                  <Badge
                    className={`${SENSOR_STATUS_MAP[sensor.status as InactiveSensorStatus].bgColor} ${SENSOR_STATUS_MAP[sensor.status as InactiveSensorStatus].textColor}`}
                  >
                    {SENSOR_STATUS_MAP[sensor.status as InactiveSensorStatus].label}
                  </Badge>
                )}
              </>
            ) : (
              <span>Sensor Details</span>
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="mt-6 text-center py-8">
            <p className="text-red-500 text-sm">Failed to load sensor details.</p>
          </div>
        ) : sensor ? (
          <div className="mt-6 space-y-5">
            {/* Identity */}
            <Section title="Identity">
              <DetailRow label="Name" value={sensor.name} />
              <DetailRow label="Sensor ID" value={sensor.mongoSensorId} />
              <DetailRow label="Client ID" value={sensor.customId} />
              <DetailRow label="Input #" value={sensor.inputNumber} />
              {sensor.description && (
                <DetailRow label="Description" value={sensor.description} />
              )}
            </Section>

            {/* Organisation & Location */}
            <Section title="Account & Location">
              <DetailRow
                label="Account"
                value={sensor.account ? `${sensor.account.name} (${sensor.account.accountNumber})` : null}
              />
              <DetailRow label="Site" value={sensor.site?.name} />
              <DetailRow label="Location" value={sensor.location?.name} />
              <DetailRow label="User Group" value={sensor.usergroupName} />
            </Section>

            {/* Stream & Device */}
            <Section title="Stream & Device">
              <DetailRow label="Stream" value={sensor.streamName} />
              <DetailRow label="Transmitter" value={sensor.transmitterName} />
              <DetailRow label="Sensor Type" value={sensor.sensorTypeName} />
              <DetailRow label="Probe Serial" value={sensor.probeSerial} />
            </Section>

            {/* Status Flags */}
            <Section title="Status">
              <div className="flex flex-wrap gap-2 py-1">
                <FlagBadge label="Billable" active={sensor.billable} />
                <FlagBadge label="Maintenance" active={sensor.maintenance} />
                <FlagBadge label="Archived" active={sensor.archived} />
              </div>
            </Section>

            {/* Dates */}
            <Section title="Dates">
              <DetailRow label="Created" value={formatDate(sensor.createdAt)} />
              <DetailRow label="First Data" value={formatDate(sensor.firstDataAt)} />
              <DetailRow label="Last Packet" value={formatDate(sensor.lastReadingAt)} />
              {sensor.archived && (
                <DetailRow label="Archived At" value={formatDate(sensor.archivedAt)} />
              )}
            </Section>

            {/* Equipment */}
            {(sensor.equipmentType || sensor.manufacturer || sensor.model || sensor.serialNumber) && (
              <Section title="Equipment">
                <DetailRow label="Type" value={sensor.equipmentType} />
                <DetailRow label="Manufacturer" value={sensor.manufacturer} />
                <DetailRow label="Model" value={sensor.model} />
                <DetailRow label="Serial #" value={sensor.serialNumber} />
              </Section>
            )}

            {/* Calibration */}
            {(sensor.calibrationCertNumber || sensor.calibrationDue) && (
              <Section title="Calibration">
                <DetailRow label="Cert #" value={sensor.calibrationCertNumber} />
                <DetailRow label="Due Date" value={formatDate(sensor.calibrationDue)} />
              </Section>
            )}

            {/* Tags & Financial */}
            {(sensor.tags.length > 0 || sensor.invoices.length > 0 || sensor.quotes.length > 0 || sensor.subscriptions.length > 0 || (sensor.invoiceAssociations?.length ?? 0) > 0 || (sensor.quoteAssociations?.length ?? 0) > 0) && (
              <Section title="Tags & Financial">
                {sensor.tags.length > 0 && (
                  <div className="py-1.5 border-b">
                    <span className="text-sm text-muted-foreground block mb-1">Tags</span>
                    <TagList items={sensor.tags} />
                  </div>
                )}
                {(sensor.invoiceAssociations?.length ?? 0) > 0 ? (
                  <div className="py-1.5 border-b">
                    <span className="text-sm text-muted-foreground block mb-1">Invoices</span>
                    <div className="flex flex-wrap gap-1">
                      {sensor.invoiceAssociations!.map((inv) => (
                        <a
                          key={inv.id}
                          href={`/erp/invoices?highlight=${inv.invoiceNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {inv.invoiceNumber}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : sensor.invoices.length > 0 ? (
                  <DetailRow label="Invoices" value={sensor.invoices.join(', ')} />
                ) : null}
                {(sensor.quoteAssociations?.length ?? 0) > 0 ? (
                  <div className="py-1.5 border-b">
                    <span className="text-sm text-muted-foreground block mb-1">Quotes</span>
                    <div className="flex flex-wrap gap-1">
                      {sensor.quoteAssociations!.map((q) => (
                        <a
                          key={q.id}
                          href={`/erp/quotes?highlight=${q.quoteNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {q.quoteNumber}
                        </a>
                      ))}
                    </div>
                  </div>
                ) : sensor.quotes.length > 0 ? (
                  <DetailRow label="Quotes" value={sensor.quotes.join(', ')} />
                ) : null}
                {sensor.subscriptions.length > 0 && (
                  <DetailRow label="Subscriptions" value={sensor.subscriptions.join(', ')} />
                )}
              </Section>
            )}

            
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
